/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo } from 'react';
import {
  Table,
  Form,
  Pagination,
  Badge,
  Button,
  Modal,
  Toast,
  Spinner,
} from 'react-bootstrap';
import { useSentAssessments } from '../../hooks/useSentAssessments';
import AssessmentScoreBar from '../assessment/AssessmentScoreBar';
import { useNavigate } from 'react-router-dom';
import { getTotalScorePercentage } from '../../util/util';
import { supabase } from '../../lib/supabase';
import { captureSupabaseError } from '../../lib/sentry';

const ITEMS_PER_PAGE = 10;

interface Props {
  buyerOrgId: string;
  supplierId?: string;
}

type SortKey =
  | 'supplier_name'
  | 'score'
  | 'completed_at'
  | 'status'
  | 'created_at';
type SortOrder = 'asc' | 'desc';

export const SentAssessmentsList = ({ buyerOrgId, supplierId }: Props) => {
  const { data, loading, refetch } = useSentAssessments(buyerOrgId, supplierId);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [toastError, setToastError] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const sortedAndFiltered = useMemo(() => {
    const filtered = data.filter((a) =>
      a.supplier_name.toLowerCase().includes(search.toLowerCase()),
    );

    filtered.sort((a, b) => {
      let aVal: any = a[sortKey];
      let bVal: any = b[sortKey];

      // Handle nulls
      if (aVal === null || aVal === undefined)
        aVal = sortOrder === 'asc' ? -Infinity : Infinity;
      if (bVal === null || bVal === undefined)
        bVal = sortOrder === 'asc' ? -Infinity : Infinity;

      if (sortKey === 'completed_at') {
        aVal = a.completed_at ? new Date(a.completed_at) : null;
        bVal = b.completed_at ? new Date(b.completed_at) : null;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data, search, sortKey, sortOrder]);

  const totalPages = Math.ceil(sortedAndFiltered.length / ITEMS_PER_PAGE);
  const paginated = sortedAndFiltered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <span className="text-muted">↕</span>;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleViewAssessment = (assessmentId: string) => {
    navigate(`/assessmentResult/${assessmentId}`);
  };

  const handleDeleteAssessment = (assessment: any) => {
    setAssessmentToDelete(assessment);
    setShowDeleteModal(true);
  };

  // 🆕 Handle actual deletion
  const handleDeleteConfirm = async () => {
    if (!assessmentToDelete) return;

    setDeleting(true);

    try {
      // UPDATE the assessment to set deleted = true
      const { error } = await supabase
        .from('assessments')
        .update({ deleted: true })
        .eq('id', assessmentToDelete.id);

      if (error) throw error;

      // Refresh the list after deletion
      await refetch();
    } catch (error) {
      captureSupabaseError(error, { op: 'assessment.delete' });
      console.error('Error:', error);
      setToastError('Failed to delete assessment. Please try again.');
    }

    setDeleting(false);
    setShowDeleteModal(false);
    setAssessmentToDelete(null);
  };

  // 🆕 Handle cancel deletion
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setAssessmentToDelete(null);
  };

  const getStatusColumn = (status: string, assessmentId: string) => {
    switch (status) {
      case 'completed':
        return (
          <>
            <Badge bg="success">Completed</Badge>
            <Button
              variant="link"
              className="p-0 ms-2"
              size="sm"
              onClick={() => handleViewAssessment(assessmentId)}
            >
              View report
            </Button>
          </>
        );
      case 'created':
        return <Badge bg="secondary">Created</Badge>;
      case 'in_progress':
        return <Badge bg="muted">In Progress</Badge>;
      default:
        return <Badge bg="muted">{status}</Badge>;
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Form.Control
        type="text"
        placeholder="Search by supplier name..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        className="mb-3 w-50"
      />

      <Table striped bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th
              onClick={() => handleSort('supplier_name')}
              style={{ cursor: 'pointer' }}
            >
              Supplier <SortIcon column="supplier_name" />
            </th>
            <th
              onClick={() => handleSort('status')}
              style={{ cursor: 'pointer' }}
            >
              Status <SortIcon column="status" />
            </th>
            <th
              onClick={() => handleSort('created_at')}
              style={{ cursor: 'pointer' }}
            >
              Sent Date <SortIcon column="created_at" />
            </th>
            <th
              onClick={() => handleSort('completed_at')}
              style={{ cursor: 'pointer' }}
            >
              Completed At <SortIcon column="completed_at" />
            </th>
            <th
              onClick={() => handleSort('score')}
              style={{ cursor: 'pointer' }}
            >
              Result <SortIcon column="score" />
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginated.map((ass) => (
            <tr key={ass.id}>
              <td>{ass.supplier_name}</td>
              <td>{getStatusColumn(ass.status, ass.id)}</td>
              <td>{new Date(ass.created_at).toLocaleDateString()}</td>
              <td>
                {ass.completed_at
                  ? new Date(ass.completed_at).toLocaleDateString()
                  : '-'}
              </td>
              <td>
                {ass.total_score ? (
                  <>
                    <AssessmentScoreBar
                      value={getTotalScorePercentage(
                        ass.total_score,
                        ass.max_score,
                      )}
                    />
                  </>
                ) : (
                  '-'
                )}
              </td>
              <td>
                {ass.status !== 'completed' && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteAssessment(ass)}
                  >
                    <i className="bi bi-trash" /> Delete
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <Pagination className="justify-content-center">
          <Pagination.Prev
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          />
          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i + 1}
              active={i + 1 === page}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          />
        </Pagination>
      )}

      {toastError && (
        <Toast
          onClose={() => setToastError(null)}
          show={!!toastError}
          delay={3000}
          autohide
        >
          <Toast.Body>{toastError}</Toast.Body>
        </Toast>
      )}

      <Modal
        show={showDeleteModal}
        onHide={handleDeleteCancel}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the assessment for{' '}
            <strong>"{assessmentToDelete?.supplier_name}"</strong>?
          </p>
          <p className="text-muted">
            This action cannot be undone. You will need to restart the
            assessment from the beginning.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleDeleteCancel}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Assessment'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
